import React, { lazy, Suspense } from "react";

import { createRoot } from "react-dom/client";

import * as Sentry from "@sentry/react";

import mixpanel from "mixpanel-browser";

import isSupported from "./supportedBrowsers";
import { FAY_API_URL } from "./fay/configuration";
import { ACTIVE_MIXPANEL_TOKEN, MIXPANEL_TOKENS } from "./mixpanelToken";
import { AxiosError } from "axios";
import { logger } from "common/logger";
import {
  waitForAnalyticsConsent,
  onAnalyticsConsentChange,
} from "common/consent/osanoConsent";

export * from "core-js/proposals/change-array-by-copy-stage-4";
export * from "core-js/stable/structured-clone";

if (process.env.NODE_ENV !== "development") {
  const tracePropagationTargets = [FAY_API_URL].map((url) => {
    return new RegExp("^" + new URL(url).origin);
  });
  Sentry.init({
    dsn: "https://5c3ac483641c4a64987b14c04d2adadd@o4505071182217216.ingest.sentry.io/4505093357371392",
    environment: process.env.NODE_ENV,
    release: process.env.REACT_APP_FAY_UI_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracePropagationTargets,
    // Performance Monitoring
    tracesSampleRate: 0,
    // Session Replay
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    // Exclude external code
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,

      // Analytics / tracking / advertising
      /google-analytics\.com/i,
      /googletagmanager\.com/i,
      /pagead2\.googlesyndication\.com/i,
      /doubleclick\.net/i,
      /facebook\.net/i,
      /connect\.facebook\.net/i,
      /cdn\.segment\.com/i,

      // Crawlers / bots / A/B testing scripts
      /bat\.bing\.com/i,
      /optimizely\.com/i,
      /hotjar\.com/i,
      /mouseflow\.com/i,
      /cdn\.ampproject\.org/i,
    ],
    ignoreErrors: [
      // _latency errors seen in prod with no actionable stack trace.
      /undefined is not an object \(evaluating '.+\._latency'\)/i,
      /Cannot read properties of undefined \(reading ['"]_latency['"]\)/i,
    ],
    beforeSend(event, hint) {
      // Skip events that are clearly from extensions or external interference [this is
      // a backup check for the denyUrls above]
      if (
        event?.exception?.values?.[0]?.stacktrace?.frames?.some((frame) =>
          /extensions|moz-extension|safari-extension|chrome-extension/.test(
            frame.filename ?? "",
          ),
        )
      ) {
        return null;
      }

      // Ignore NetworkErrors; we don't want to spam Sentry with these, plus we
      // track our API availability separately!
      const error = hint?.originalException;
      if (error instanceof Error && error.name === "NetworkError") {
        return null;
      }

      // AxiosErrors have a lot of information that are hidden from us in
      // Sentry!
      // The error does have more information on it, such as:
      // - Request configuration https://axios-http.com/docs/req_config
      //   - We can get the `url`, `method`, and `params` from this
      // - The HTTP response code
      // Extracting these can give us more information on triaging hundreds of
      // these errors.
      if (error instanceof AxiosError) {
        const url = error.config.url
          ? new URL(error.config.url).pathname
          : "?url?";
        const method = error.config.method ?? "?method?";
        const httpResponseCode = error.response?.status;
        const responseData = error.response?.data;

        event.message = `Network error from ${method} ${url}: HTTP Response Code ${httpResponseCode}`;
        event.extra = {
          ...(event.extra ?? {}),
          responseData,
        };
        event.fingerprint = [url, method, httpResponseCode];

        // If this is a "Network Error", we should not report it.
        // These are usually device level internet issues.
        if (error.message === "Network Error") {
          logger.warn("Not reporting network error to sentry", error);
          return null;
        }
      }

      return event;
    },
  });
}

// This allows us to see (non-input) text in Session Replays.
const isSignupApp = process.env.REACT_APP_FAY_APP === "signup";
const mixpanelConfig = {
  record_mask_text_selector: "",
  // Start opted-out for signup app until Osano consent is granted
  opt_out_tracking_by_default: isSignupApp,
};

if (!!ACTIVE_MIXPANEL_TOKEN) {
  mixpanel.init(ACTIVE_MIXPANEL_TOKEN, mixpanelConfig);

  // Handle Osano consent for signup app (Osano not loaded elsewhere)
  if (isSignupApp) {
    // Wait for initial consent decision
    waitForAnalyticsConsent().then((status) => {
      if (status === "granted") {
        mixpanel.opt_in_tracking();
        if (mixpanel.fayClient) {
          mixpanel.fayClient.opt_in_tracking();
        }
      }
    });

    // Handle consent revocation
    onAnalyticsConsentChange((status) => {
      if (status === "granted") {
        mixpanel.opt_in_tracking();
        if (mixpanel.fayClient) {
          mixpanel.fayClient.opt_in_tracking();
        }
      } else {
        mixpanel.opt_out_tracking();
        if (mixpanel.fayClient) {
          mixpanel.fayClient.opt_out_tracking();
        }
      }
    });
  }

  const userAgentBotTest = navigator.userAgent;
  mixpanel.register({ "User Agent": userAgentBotTest });
  const ignoredAgents = [/Datadog\/Synthetics/i, /Cypress/i];
  if (
    ignoredAgents.some((ignoredAgent) => ignoredAgent.test(userAgentBotTest))
  ) {
    mixpanel.register({ $ignore: true });
  }
}

// Initialize secondary Mixpanel instance for Fay Client project (signup app only)
if (isSignupApp && MIXPANEL_TOKENS.client) {
  mixpanel.init(MIXPANEL_TOKENS.client, mixpanelConfig, "fayClient");
}

const root = createRoot(document.getElementById("root"));

const App = lazy(() => {
  if (process.env.REACT_APP_FAY_APP === "provider") {
    return import("./ProviderApp");
  } else if (process.env.REACT_APP_FAY_APP === "client") {
    return import("./ClientApp");
  } else if (process.env.REACT_APP_FAY_APP === "signup") {
    return import("./SignupApp");
  }

  return Promise.reject(new Error("Invalid app type"));
});

root.render(
  <React.StrictMode>
    <Suspense>
      <App />
    </Suspense>
  </React.StrictMode>,
);

// Check if browser is supported.
// Generated using https://github.com/browserslist/browserslist-useragent-regexp
// Higher versions are allowed.
// To update the list, run
// `npx browserslist-useragent-regexp` and then
// `npm run supportedBrowsers`
if (!isSupported.test(navigator.userAgent)) {
  alert(
    "Critical features of Fay will not work properly because your web browser's version is old. Please upgrade your web browser to the latest version.",
  );

  // Polyfills for people who decide to ignore this warning.

  // eslint-disable-next-line no-extend-native
  Array.prototype.at = function (index) {
    return this[index];
  };

  // eslint-disable-next-line no-extend-native
  Array.prototype.findLast = function (predicate) {
    for (let i = this.length - 1; i >= 0; i--) {
      if (predicate(this[i], i, this)) {
        return this[i];
      }
    }
    return undefined;
  };

  if (!Object.hasOwn) {
    Object.hasOwn = (obj, prop) =>
      Object.prototype.hasOwnProperty.call(obj, prop);
  }
}
